/**
 * 用户列表。
 *
 * 管理端以「用户 × 梦灵」的关系为主记录，同时呈现账号身份和业务足迹。
 * 羁绊、好感、日记、摘要与 token 都属于具体关系，不做跨梦灵的伪合计。
 *
 * 顶栏的角色切换器会把 characterId 带进来。
 */

import { useState } from "react";
import { Avatar, Input, InputNumber, Select, Space, Tooltip, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DataTable, useTableParams } from "../../components/DataTable";
import { useCharacterScope } from "../../services/character";
import { fmtNumber, RelativeTime, StatusTag } from "../../components/format";
import {
  PowerGrantModal,
  PowerGrantPlus,
  type PowerGrantTarget,
} from "../../components/PowerGrant";

interface RelationshipRow {
  id: string;
  userId: string;
  userName: string | null;
  userAvatarUrl: string | null;
  userCreatedAt: string | null;
  userLastActiveAt: string | null;
  characterName: string;
  status: string;
  bond: number;
  level: number;
  characterAffinity: number;
  userPreferredName: string | null;
  selfIntroduction: string | null;
  power: number;
  conversationCount: number;
  messageCount: number;
  diaryCount: number;
  memoryCount: number;
  totalTokens: number;
  startedAt: string | null;
  lastInteractionAt: string | null;
}

function UserAvatar({ url, name }: { url: string | null; name: string | null }) {
  const [broken, setBroken] = useState(false);
  const initial = name?.trim().slice(0, 1);
  const style = { background: "var(--miao-brand-300)", color: "#fff" } as const;
  if (!url || broken) {
    return (
      <Avatar size={26} style={style} icon={initial ? undefined : <UserOutlined />}>
        {initial}
      </Avatar>
    );
  }
  return (
    <Avatar size={26} src={url} style={style} onError={() => { setBroken(true); return false; }}>
      {initial}
    </Avatar>
  );
}

const FILTERS = ["search", "status", "userStatus", "adminsOnly", "minBond", "minLevel"];

export function RelationshipsPage() {
  const scope = useCharacterScope();
  const queryClient = useQueryClient();
  const [grantTarget, setGrantTarget] = useState<PowerGrantTarget | null>(null);
  const { searchParams, setParam } = useTableParams(FILTERS);

  return (
    <>
      <DataTable<RelationshipRow>
      title="用户"
      path="/api/v1/admin/relationships"
      fixedParams={scope}
      filterKeys={FILTERS}
      serverSort={{ default: "lastInteractionAt", defaultOrder: "desc" }}
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          点列头排序 · 总 token 为当前页统计，不参与排序
        </Typography.Text>
      }
      toolbar={
        <Space wrap>
          <Input.Search
            allowClear
            style={{ width: 280 }}
            placeholder="昵称 / 手机号 / 用户 ID / 称谓 / 自我介绍 / 期望"
            defaultValue={searchParams.get("search") ?? ""}
            onSearch={(v) => setParam("search", v)}
          />
          <Select
            allowClear
            style={{ width: 130 }}
            placeholder="关系状态"
            value={searchParams.get("status") ?? undefined}
            onChange={(v) => setParam("status", v)}
            options={[
              { value: "active", label: "active" },
              { value: "archived", label: "archived" },
            ]}
          />
          <Select
            allowClear
            style={{ width: 140 }}
            placeholder="账号状态"
            value={searchParams.get("userStatus") ?? undefined}
            onChange={(v) => setParam("userStatus", v)}
            options={[
              { value: "active", label: "active 正常" },
              { value: "suspended", label: "suspended 停用" },
              { value: "deletion_requested", label: "deletion_requested 注销中" },
              { value: "deleted", label: "deleted 已注销" },
            ]}
          />
          <Select
            allowClear
            style={{ width: 130 }}
            placeholder="仅管理员"
            value={searchParams.get("adminsOnly") ?? undefined}
            onChange={(v) => setParam("adminsOnly", v)}
            options={[{ value: "true", label: "仅管理员" }]}
          />
          <InputNumber
            style={{ width: 130 }}
            placeholder="羁绊 ≥"
            value={searchParams.get("minBond") ? Number(searchParams.get("minBond")) : null}
            onChange={(v) => setParam("minBond", v)}
          />
          <InputNumber
            style={{ width: 120 }}
            placeholder="等级 ≥"
            value={searchParams.get("minLevel") ? Number(searchParams.get("minLevel")) : null}
            onChange={(v) => setParam("minLevel", v)}
          />
        </Space>
      }
      columns={[
        {
          title: "",
          key: "avatar",
          width: 42,
          align: "center",
          render: (_: unknown, r: RelationshipRow) => (
            <UserAvatar url={r.userAvatarUrl} name={r.userName} />
          ),
        },
        {
          title: "用户",
          dataIndex: "userName",
          width: 150,
          render: (v: string | null, r) => (
            <Space size={4}>
              <Link to={`/users/${r.id}`}>{v || "(未命名)"}</Link>
            </Space>
          ),
        },
        {
          // 称谓 = 用户希望角色怎么称呼自己。旧库那个 `缈缈->张三` 的 title 已废弃。
          title: "称谓",
          dataIndex: "userPreferredName",
          width: 120,
          render: (v: string | null) => v || <span style={{ color: "#bbb" }}>—</span>,
        },
        {
          title: "自我介绍",
          dataIndex: "selfIntroduction",
          width: 220,
          render: (v: string | null) =>
            v ? <Tooltip title={v}><span>{v}</span></Tooltip> : <span style={{ color: "#bbb" }}>—</span>,
        },
        { title: "羁绊", key: "bond", dataIndex: "bond", width: 95, align: "right", sorter: true, render: fmtNumber },
        { title: "等级", key: "level", dataIndex: "level", width: 75, align: "right", sorter: true },
        {
          title: "好感",
          key: "characterAffinity",
          dataIndex: "characterAffinity",
          width: 100,
          align: "right",
          sorter: true,
          // 旧系统按对话量加权放大的公式会突破 ±10，存量里有几万的值。
          render: (v: number) =>
            v > 1000 ? (
              <Tooltip title="旧系统加权公式遗留的越界值，新系统已钳制在 ±10">
                <span style={{ color: "#B4232E" }}>{fmtNumber(v)}</span>
              </Tooltip>
            ) : (
              fmtNumber(v)
            ),
        },
        {
          title: "灵力",
          key: "power",
          dataIndex: "power",
          width: 105,
          align: "right",
          sorter: true,
          render: (value: number, row: RelationshipRow) => (
            <Space size={2}>
              <span>{fmtNumber(value)}</span>
              <PowerGrantPlus
                onClick={() =>
                  setGrantTarget({
                    userId: row.userId,
                    userName: row.userName,
                    relationshipId: row.id,
                    characterName: row.characterName,
                    power: row.power,
                  })
                }
              />
            </Space>
          ),
        },
        {
          title: "会话",
          key: "conversationCount",
          dataIndex: "conversationCount",
          width: 85,
          align: "right",
          sorter: true,
          render: fmtNumber,
        },
        {
          title: "消息",
          key: "messageCount",
          dataIndex: "messageCount",
          width: 90,
          align: "right",
          sorter: true,
          render: fmtNumber,
        },
        { title: "摘要", dataIndex: "memoryCount", width: 85, align: "right", render: fmtNumber },
        { title: "日记", dataIndex: "diaryCount", width: 85, align: "right", render: fmtNumber },
        {
          title: "总 token",
          dataIndex: "totalTokens",
          width: 110,
          align: "right",
          render: (v: number) => (
            <Tooltip title="这段关系所有中枢决策的输入 + 输出 token 之和">
              <span>{fmtNumber(v)}</span>
            </Tooltip>
          ),
        },
        { title: "关系状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        {
          title: "最后活跃",
          key: "userLastActiveAt",
          dataIndex: "userLastActiveAt",
          width: 125,
          render: (v) => <RelativeTime value={v} />,
        },
        {
          title: "最后互动",
          key: "lastInteractionAt",
          dataIndex: "lastInteractionAt",
          width: 125,
          sorter: true,
          render: (v) => <RelativeTime value={v} />,
        },
        {
          title: "关系建立",
          key: "startedAt",
          dataIndex: "startedAt",
          width: 125,
          sorter: true,
          render: (v) => <RelativeTime value={v} />,
        },
        {
          title: "注册",
          key: "userCreatedAt",
          dataIndex: "userCreatedAt",
          width: 125,
          sorter: true,
          render: (v) => <RelativeTime value={v} />,
        },

      ]}
      />
      <PowerGrantModal
        target={grantTarget}
        onClose={() => setGrantTarget(null)}
        onGranted={() => {
          void queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/relationships"] });
          void queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/users"] });
        }}
      />
    </>
  );
}
